import React, { useState, useCallback, useMemo } from 'react';
import {
    Box,
    useTheme,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useLocation } from 'react-router';
import { skillAPI, membershipAPI, physicalRecordAPI, logAPI, recommendationAPI, contentAPI } from '../../actions/imsServer';
import { isAdmin, formatDateTime, getLocalizedName } from '../../utils/base';
import { API_PAGE_SIZE } from '../../constants';
import Dropdown from '../../components/dropdown';
import ContentArea from '../../components/contentArea';
import { Dashboard, StepCount, HeartRate, DidimActivity, Challenge, PointIcon, Championship, RankingIcon, BasicInfoIcon } from '../../assets/images';
import {
    DashboardTab,
    StepTab,
    HeartRateTab,
    DidimActivityTab,
    ChallengeTab,
    PointTab,
    ChampionshipTab,
    RankingTab,
    BasicInfoTab
} from './components/tabs';

export const UserDetail = () => {
    const location = useLocation();
    const theme = useTheme();
    const { t, i18n } = useTranslation();

    const [userData, setUserData] = useState(null);
    const [selectedOrganization, setSelectedOrganization] = useState('');
    const [selectedMenu, setSelectedMenu] = useState(0);
    const [organizations, setOrganizations] = useState([]);
    const [memberships, setMemberships] = useState([]);
    const [physicalRecord, setPhysicalRecord] = useState(null);
    const [physicalRecords, setPhysicalRecords] = useState([]); // 배열로 관리
    const [playLogsData, setPlayLogsData] = useState(null);
    const [skillData, setSkillData] = useState(null);
    const [recommendationData, setRecommendationData] = useState(null);

    // 조직 데이터 가져오기
    const fetchOrganizations = async (userId) => {
        try {
            const response = await membershipAPI.getMemberships({
                userId: userId,
                type: 1,
                state: 0,
                expand: 'organization'
            });

            // userData에서 mainOrganizationId 가져오기
            const rawUserData = location.state?.userData;
            const mainOrgId = rawUserData?.mainOrganizationId || rawUserData?.mainorganizationid;

            // 멤버십 데이터 저장
            const membershipsData = Array.isArray(response.data.memberships) ? response.data.memberships : [];
            setMemberships(membershipsData);

            // admin 여부 확인 (meInfo에서 grade 확인)
            const meInfoString = localStorage.getItem('meInfo');
            const meInfo = JSON.parse(meInfoString);
            let adminMainOrgId = null;
            if (meInfoString) {
                try {
                    // 어드민인 경우 기관 가져오기
                    if (isAdmin(meInfo?.grade) && meInfo.id) {
                        try {
                            // grade 50, 60 모두 membership에서 가져오기
                            const membershipsResponse = await membershipAPI.getMemberships({ userId: meInfo.id, type: 0, state: 0, expand: 'organization' });
                            const memberships = membershipsResponse?.data?.memberships || [];
                            if (memberships.length > 0) {
                                adminMainOrgId = memberships[0].organizationId;
                            }
                        } catch (orgErr) {
                            console.error('어드민 기관 정보 가져오기 실패:', orgErr);
                        }
                    } else {
                        // 일반 사용자는 meInfo의 mainOrganizationId 사용
                        adminMainOrgId = meInfo.mainOrganizationId || meInfo.mainorganizationid;
                    }
                } catch (e) {
                    console.error('meInfo 파싱 실패:', e);
                }
            }

            // 조직 데이터를 드롭다운 옵션 형태로 변환
            const orgOptions = [];
            if (Array.isArray(response.data.memberships)) {
                response.data.memberships.forEach(membership => {
                    if (membership.organization) {
                        // admin이면 본인의 기관만 필터링
                        if (isAdmin(meInfo?.grade) && adminMainOrgId) {
                            if (String(membership.organizationId) !== String(adminMainOrgId)) {
                                return; // 본인 기관이 아니면 제외
                            }
                        }
                        orgOptions.push({
                            value: membership.organizationId,
                            label: membership.organization.name,
                            isMain: membership.organizationId === mainOrgId
                        });
                    }
                });
            }

            // 메인기관을 상위에 정렬
            const sortedOrgOptions = orgOptions.sort((a, b) => {
                if (a.isMain && !b.isMain) return -1;
                if (!a.isMain && b.isMain) return 1;
                return 0;
            });

            setOrganizations(sortedOrgOptions);

            // location.state에서 전달받은 organizationId 확인
            const passedOrganizationId = location.state?.organizationId;

            // admin이면 본인 기관을 기본 선택, 아니면 메인기관 선택
            if (sortedOrgOptions.length > 0) {
                // 전달받은 organizationId가 있으면 우선적으로 선택
                if (passedOrganizationId) {
                    const passedOrg = sortedOrgOptions.find(org => String(org.value) === String(passedOrganizationId));
                    if (passedOrg && !selectedOrganization) {
                        setSelectedOrganization(passedOrg.value);
                        return; // 전달받은 기관을 선택했으면 종료
                    }
                }

                if (isAdmin(meInfo?.grade) && adminMainOrgId) {
                    // admin이면 본인 기관 선택
                    const adminOrg = sortedOrgOptions.find(org => String(org.value) === String(adminMainOrgId));
                    if (adminOrg && !selectedOrganization) {
                        setSelectedOrganization(adminOrg.value);
                    } else if (!selectedOrganization && sortedOrgOptions[0]) {
                        setSelectedOrganization(sortedOrgOptions[0].value);
                    }
                } else {
                    // 일반 사용자는 메인기관 선택
                    const mainOrg = sortedOrgOptions.find(org => org.isMain);
                    if (mainOrg && !selectedOrganization) {
                        setSelectedOrganization(mainOrg.value);
                    } else if (!selectedOrganization && sortedOrgOptions[0]) {
                        // 메인기관이 없으면 첫 번째 기관 선택
                        setSelectedOrganization(sortedOrgOptions[0].value);
                    }
                }
            }
        } catch (err) {
            console.error('조직 데이터 로드 실패:', err);
            setOrganizations([]);
        }
    };

    // 신체 기록 데이터 가져오기
    const fetchPhysicalRecords = async (userId) => {
        try {
            const today = new Date();
            const startDate = new Date(today);
            startDate.setDate(startDate.getDate() - 89); // 과거 3개월 (90일) - 더 많은 데이터 로드
            startDate.setHours(0, 0, 0, 0);

            const endDate = new Date(today);
            endDate.setHours(23, 59, 59, 999);

            const response = await physicalRecordAPI.getPhysicalRecords({
                userId: userId,
                state: 0,
                startDate: formatDateTime(startDate, '-', ':', 0, 10),
                endDate: formatDateTime(endDate, '-', ':', 0, 10)
            });

            if (response?.data?.physicalRecords) {
                setPhysicalRecords(response.data.physicalRecords);
                // 최근 기록도 설정 (하위 호환성)
                setPhysicalRecord(response.data.physicalRecords[response.data.physicalRecords.length - 1] || null);
            } else {
                setPhysicalRecords([]);
                setPhysicalRecord(null);
            }
        } catch (err) {
            console.error('신체 기록 데이터 로드 실패:', err);
            setPhysicalRecords([]);
            setPhysicalRecord(null);
        }
    };

    // 플레이 로그 데이터 가져오기
    const fetchPlayLogs = async (userId, startDate = null, endDate = null) => {
        try {
            const today = new Date();
            const start = startDate || (() => {
                const date = new Date(today);
                date.setDate(date.getDate() - 89); // 과거 3개월 (90일) - 더 많은 데이터 로드
                date.setHours(0, 0, 0, 0);
                return date;
            })();
            const end = endDate || (() => {
                const date = new Date(today);
                date.setHours(23, 59, 59, 999);
                return date;
            })();

            const startTime = start.toISOString();
            const endTime = end.toISOString();
            let allLogs = [];
            let offset = 0;
            let page;
            do {
                const response = await logAPI.getPlayLogs({
                    userId,
                    startTime,
                    endTime,
                    expand: 'organization',
                    offset
                });
                page = response?.data?.logs || [];
                allLogs = allLogs.concat(page);
                offset += API_PAGE_SIZE;
            } while (page.length === API_PAGE_SIZE);

            if (allLogs.length > 0) {
                // 기존 데이터와 병합 (중복 제거)
                setPlayLogsData(prevLogs => {
                    if (!prevLogs) return allLogs;
                    const existingIds = new Set(prevLogs.map(log => log.id));
                    const uniqueNewLogs = allLogs.filter(log => !existingIds.has(log.id));
                    return [...prevLogs, ...uniqueNewLogs].sort((a, b) => {
                        const dateA = new Date(a.time || a.startTime);
                        const dateB = new Date(b.time || b.startTime);
                        return dateA - dateB;
                    });
                });
            }
        } catch (err) {
            console.error('플레이 로그 데이터 로드 실패:', err);
            // 에러 발생 시 기존 데이터 유지
        }
    };

    // 체력 지표 데이터 가져오기
    const fetchSkill = async (userId) => {
        try {
            const response = await skillAPI.getSkills({
                userId: userId
            });

            if (response?.data?.skills) {
                setSkillData(response.data.skills);
            }
        } catch (err) {
            console.error('스킬 데이터 로드 실패:', err);
            setSkillData(null);
        }
    };

    // AI 추천 데이터 가져오기
    const fetchRecommendation = async (userId) => {
        try {
            const fetchContentsBatched = async () => {
                let all = [];
                let offset = 0;
                let page;
                do {
                    const res = await contentAPI.getContents({ state: 0, offset });
                    page = res?.data?.contents || [];
                    all = all.concat(page);
                    offset += API_PAGE_SIZE;
                } while (page.length === API_PAGE_SIZE);
                return all;
            };

            const [recommendationResponse, contents] = await Promise.all([
                recommendationAPI.getRecommendations({ userId: userId }),
                fetchContentsBatched()
            ]);

            // AI 추천 데이터 처리
            const fitnessData = recommendationResponse?.data?.recommendations?.[0]?.fitness;
            if (fitnessData && Array.isArray(fitnessData)) {
                // contentIndex로 contents 매핑
                const contentMap = new Map((contents || []).map(c => [c.index, c]));
                const processedRecommendation = fitnessData.map(item => {
                    const content = contentMap.get(item.contentIndex);
                    return {
                        contentIndex: item.contentIndex,
                        name: (content && getLocalizedName(content, i18n.language)) || content?.name || t('exercise'),
                        icon: content?.icon || null,
                        circuit: item.circuit || '',
                        comment: item.comment || '',
                    };
                });
                setRecommendationData(processedRecommendation);
            } else if (fitnessData) {
                // 기존 형식 (하위 호환성)
                setRecommendationData(fitnessData);
            } else {
                setRecommendationData([]);
            }
        } catch (err) {
            console.error('AI 추천 데이터 로드 실패:', err);
            setRecommendationData([]);
        }
    };

    // 전달받은 데이터를 위젯 형태로 변환하는 함수
    const transformUserData = useCallback((rawData, recommendation = null) => {
        return {
            id: rawData.id,
            loginId: rawData.loginId,
            name: rawData.name || '',
            birth: rawData.birth,
            mainOrganizationId: rawData.mainOrganizationId,
            level: rawData.level || 1,
            exp: rawData.exp || 0,
            point: rawData.point || 0,
            profilePath: rawData?.profilePath,
            recommendation
        };
    }, [t]);

    // 사용자 데이터를 변환하는 함수
    const updateUserData = useCallback(() => {
        if (location.state?.userData) {
            setUserData(transformUserData(
                location.state.userData,
                recommendationData
            ));
        }
    }, [location.state?.userData, recommendationData, transformUserData]);

    // 초기 데이터 로드 (fetch 함수는 매 렌더 새 참조라 의존성에서 제외 → 반복 호출 방지)
    React.useEffect(() => {
        const userId = location.state?.userData?.id || location.state?.id;
        if (userId) {
            fetchOrganizations(userId);
            fetchPhysicalRecords(userId);
            fetchPlayLogs(userId);
            fetchSkill(userId);
            fetchRecommendation(userId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch* 는 일반 함수
    }, [location.state?.userData?.id, location.state?.id]);

    // physicalRecord가 업데이트되면 userData도 업데이트
    React.useEffect(() => {
        updateUserData();
    }, [updateUserData]);

    // 선택된 기관에 맞는 체력 지표 데이터 필터링
    const filteredSkillData = useMemo(() => {
        if (!skillData) {
            return {
                endurance: 0,
                strength: 0,
                balance: 0,
                flexibility: 0,
                reaction: 0,
                cooperation: 0,
            };
        }

        // skillData가 배열인 경우
        const skills = Array.isArray(skillData) ? skillData : [skillData];

        // 선택된 기관에 맞는 skill 데이터 찾기
        const filteredSkill = skills.find(skill => {
            const skillOrgId = skill?.organizationId;
            if (!selectedOrganization) {
                return skillOrgId === 0 || skillOrgId === '0' || !skillOrgId;
            }
            return String(skillOrgId) === String(selectedOrganization);
        });

        return {
            endurance: filteredSkill?.endurance || 0,
            strength: filteredSkill?.strength || 0,
            balance: filteredSkill?.balance || 0,
            flexibility: filteredSkill?.flexibility || 0,
            reaction: filteredSkill?.reaction || 0,
            cooperation: filteredSkill?.cooperation || 0,
        };
    }, [skillData, selectedOrganization]);

    // 네비게이션 메뉴 아이템들
    const menuItems = [
        t('dashboard'),
        t('step_count'),
        t('heart_rate'),
        t('didim_activity'),
        t('challenge'),
        t('points'),
        t('championship'),
        t('ranking'),
        t('basic_info')
    ];

    // 메뉴에 따른 컴포넌트 렌더링
    const renderSelectedComponent = () => {
        switch (selectedMenu) {
            case 1: // 디딤수
                return (
                    <StepTab
                        physicalRecords={physicalRecords}
                    />
                );
            case 2: // 심박수
                return (
                    <HeartRateTab
                        physicalRecords={physicalRecords}
                    />
                );
            case 3: // 디딤활동
                // 선택된 기관의 playLogsData 필터링
                const filteredDidimPlayLogsData = selectedOrganization && playLogsData
                    ? playLogsData.filter(log => String(log.organizationId) === String(selectedOrganization))
                    : playLogsData || [];

                return (
                    <DidimActivityTab
                        userData={userData}
                        playLogsData={filteredDidimPlayLogsData}
                        selectedOrganization={selectedOrganization}
                    />
                );
            case 4: // 챌린지
                return (
                    <ChallengeTab
                        userData={userData}
                    />
                );
            case 5: // 포인트
                return (
                    <PointTab
                        userData={userData}
                    />
                );
            case 6: // 챔피언쉽
                return (
                    <ChampionshipTab
                        userData={userData}
                        selectedOrganization={selectedOrganization}
                    />
                );
            case 7: // 랭킹
                return (
                    <RankingTab
                        userData={userData}
                        memberships={memberships}
                        selectedOrganization={selectedOrganization}
                    />
                );
            case 8: // 기본정보
                return (
                    <BasicInfoTab
                        userData={userData}
                    />
                );
            default:
                // 선택된 기관에 해당하는 membership 찾기
                const currentMembership = memberships.find(
                    m => m.organizationId === selectedOrganization
                ) || null;

                // 선택된 기관의 playLogsData 필터링
                const filteredPlayLogsData = selectedOrganization && playLogsData
                    ? playLogsData.filter(log => log.organizationId === selectedOrganization)
                    : [];

                return (
                    <DashboardTab
                        userData={userData}
                        physicalRecord={physicalRecord}
                        membership={currentMembership}
                        playLogsData={filteredPlayLogsData}
                        organizationName={currentMembership?.organization?.name || ''}
                        skill={filteredSkillData}
                    />
                );
        }
    };

    return (
        <ContentArea>
            {/* 메인 콘텐츠 */}
            <Box sx={{
                display: 'flex',
                pl: 0,
                pr: 2.5,
                pt: 2.5,
                pb: 2.5,
                gap: 1,
                width: '100%'
            }}>
                {/* 왼쪽 네비게이션 메뉴 */}
                <Box sx={{
                    width: '170px',
                    display: { xs: 'none', md: 'flex' },
                    flexDirection: 'column',
                    gap: 1,
                    pr: 2,
                    position: 'relative',
                }}>
                    {/* 사용자 이름 표시 */}
                    {userData?.name && (
                        <Box sx={{
                            width: 'fit-content',
                            minWidth: '158px',
                            padding: '12px 8px',
                            borderRadius: '13px',
                            backgroundColor: theme.palette.figma.white[60],
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '8px',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            zIndex: 10,
                        }}>
                            <Typography
                                sx={{
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: theme.palette.neutral.white,
                                    letterSpacing: '-0.42px',
                                    textAlign: 'center',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {userData.name}
                            </Typography>
                        </Box>
                    )}
                    <Box sx={{
                        borderRadius: '15px',
                        padding: '2px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        transition: 'all 0.2s ease-in-out',
                        marginTop: '60px' // 이름 박스 높이만큼 여백 추가
                    }}>
                        {menuItems.map((item, index) => (
                            <Box
                                key={index}
                                sx={{
                                    flex: 1,
                                    width: '100%',
                                    minHeight: '40px',
                                    position: 'relative',
                                    borderRadius: '13px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                    px: 1.5,
                                    cursor: 'pointer',
                                    backgroundColor: selectedMenu === index ? theme.palette.figma.block : 'transparent',
                                    transition: 'all 0.2s ease-in-out',
                                    marginBottom: index < menuItems.length - 1 ? '4px' : 0,
                                    '&:hover': {
                                        backgroundColor: selectedMenu === index
                                            ? theme.palette.figma.block
                                            : theme.palette.figma.white[5],
                                    },
                                }}
                                onClick={() => setSelectedMenu(index)}
                            >
                                {index === 0 && (
                                    <Dashboard
                                        size={16}
                                        color={selectedMenu === index ? theme.palette.figma.white[100] : theme.palette.figma.white[80]}
                                        style={{ flexShrink: 0, marginRight: '8px' }}
                                    />
                                )}
                                {index === 1 && (
                                    <StepCount
                                        size={16}
                                        color={selectedMenu === index ? theme.palette.figma.white[100] : theme.palette.figma.white[80]}
                                        style={{ flexShrink: 0, marginRight: '8px' }}
                                    />
                                )}
                                {index === 2 && (
                                    <HeartRate
                                        size={16}
                                        color={selectedMenu === index ? theme.palette.figma.white[100] : theme.palette.figma.white[80]}
                                        style={{ flexShrink: 0, marginRight: '8px' }}
                                    />
                                )}
                                {index === 3 && (
                                    <DidimActivity
                                        size={16}
                                        color={selectedMenu === index ? theme.palette.figma.white[100] : theme.palette.figma.white[80]}
                                        style={{ flexShrink: 0, marginRight: '8px' }}
                                    />
                                )}
                                {index === 4 && (
                                    <Challenge
                                        size={16}
                                        color={selectedMenu === index ? theme.palette.figma.white[100] : theme.palette.figma.white[80]}
                                        style={{ flexShrink: 0, marginRight: '8px' }}
                                    />
                                )}
                                {index === 5 && (
                                    <PointIcon
                                        size={16}
                                        color={selectedMenu === index ? theme.palette.figma.white[100] : theme.palette.figma.white[80]}
                                        style={{ flexShrink: 0, marginRight: '8px' }}
                                    />
                                )}
                                {index === 6 && (
                                    <Championship
                                        size={16}
                                        color={selectedMenu === index ? theme.palette.figma.white[100] : theme.palette.figma.white[80]}
                                        style={{ flexShrink: 0, marginRight: '8px' }}
                                    />
                                )}
                                {index === 7 && (
                                    <RankingIcon
                                        size={16}
                                        color={selectedMenu === index ? theme.palette.figma.white[100] : theme.palette.figma.white[80]}
                                        style={{ flexShrink: 0, marginRight: '8px' }}
                                    />
                                )}
                                {index === 8 && (
                                    <BasicInfoIcon
                                        size={16}
                                        color={selectedMenu === index ? theme.palette.figma.white[100] : theme.palette.figma.white[80]}
                                        style={{ flexShrink: 0, marginRight: '8px' }}
                                    />
                                )}
                                <Typography
                                    sx={{
                                        fontSize: '14px',
                                        fontWeight: selectedMenu === index ? 600 : 400,
                                        color: selectedMenu === index
                                            ? theme.palette.figma.white[100]
                                            : theme.palette.figma.white[80],
                                        letterSpacing: '-0.42px',
                                        textAlign: 'left',
                                        whiteSpace: 'nowrap',
                                        transition: 'all 0.2s ease-in-out',
                                    }}
                                >
                                    {item ? item.charAt(0).toUpperCase() + item.slice(1) : item}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* 학교 선택 드롭다운 */}
                    {(selectedMenu === 0 || selectedMenu === 3 || selectedMenu === 6 || selectedMenu === 7) && <Box sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        pt: 2.5,
                        px: 2.5,
                        width: '100%'
                    }}>
                        <Dropdown
                            options={organizations}
                            value={selectedOrganization}
                            onChange={(option) => setSelectedOrganization(option.value)}
                            placeholder={t('select_organization')}
                        />
                    </Box>}
                    {renderSelectedComponent()}
                </Box>
            </Box>

        </ContentArea >
    );
};
